import type { FC } from 'react';
import React, { useCallback, useMemo } from 'react';
import {
  Button,
  FormControlLabel,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import type { FormikHelpers, FormikProps } from 'formik';
import { getIn, useFormik } from 'formik';
import {
  CountrySelector,
  FCheckbox,
  FDatePicker,
  FTextInput,
} from '@pflegenavi/web-components';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useFamilyMemberSignupValidationSchema } from '@pflegenavi/shared/frontend';
import { WelcomeSideBar } from '../general/WelcomeSideBar';
import type { Country } from '@pflegenavi/frontend/api-nursing-home';
import { ResidentRelationshipOptions } from './ResidentRelationshipOptions';
import type { FamilyMemberResidentRelationship } from '@pflegenavi/shared/api';

export interface ISignupFormValues {
  firstName: string;
  lastName: string;
  birthdate: Date | undefined;
  nationalityId: string | undefined;
  countryOfResidenceId: string | undefined;
  relationship: FamilyMemberResidentRelationship | undefined;
  acceptTerms: boolean;
}

interface SignupFormProps {
  userDetails: {
    firstName: string;
    lastName: string;
  };
  countries: Country[];
  loading: boolean;
  handleSignup: (values: ISignupFormValues) => Promise<void>;
}

export const SignupForm: FC<SignupFormProps> = ({
  userDetails,
  countries,
  loading,
  handleSignup,
}) => {
  const { t } = useTranslation();

  const validationSchema = Yup.object(
    useFamilyMemberSignupValidationSchema()
  ).required();

  const handleSubmit = useCallback(
    async (
      values: ISignupFormValues,
      helpers: FormikHelpers<ISignupFormValues>
    ) => {
      try {
        await handleSignup(values);
      } finally {
        helpers.setSubmitting(false);
      }
    },
    [handleSignup]
  );

  const initialValues: ISignupFormValues = useMemo(() => {
    return {
      firstName: userDetails.firstName,
      lastName: userDetails.lastName,
      birthdate: undefined,
      nationalityId: undefined,
      countryOfResidenceId: undefined,
      relationship: undefined,
      acceptTerms: false,
    };
  }, [userDetails]);

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    onSubmit: handleSubmit,
    validationSchema: validationSchema,
    validateOnMount: true,
  });

  const handleSaveButtonClick = useCallback(
    async (acceptInvite?: boolean) => {
      await formik.submitForm();
    },
    [formik]
  );

  return (
    <Grid container height="100vh" overflow="hidden">
      <Grid item md={3} height="100vh">
        <WelcomeSideBar
          title={t('mangopay.signup.welcome-title')}
          subtitle={t('mangopay.signup.welcome-subtitle')}
        />
      </Grid>
      <Grid
        item
        md={9}
        height="100vh"
        overflow="auto"
        position="relative"
        pb={10}
        pt={5}
      >
        <Stack
          justifyContent="center"
          alignItems="center"
          sx={{
            width: '100%',
            height: '100vh',
          }}
        >
          <Stack sx={{ maxWidth: 500 }} gap={3}>
            <Stack gap={0.5}>
              <Typography variant="h5" color="primary.main">
                {t('mangopay.signup.signup-title')}
              </Typography>
              <Typography variant="subtitle2" color="grey.500">
                {t('mangopay.signup.signup-description')}
              </Typography>
            </Stack>
            <Stack gap={2}>
              <SignupFormInputs
                formik={formik}
                countries={countries}
                disabled={formik.isSubmitting || loading}
              />

              <Stack direction="row" justifyContent="center">
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => handleSaveButtonClick(true)}
                  disabled={!formik.isValid || formik.isSubmitting || loading}
                  fullWidth
                >
                  {t('mangopay.signup.form.signup-button')}
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </Grid>
    </Grid>
  );
};

interface SignupFormInputsProps {
  formik: FormikProps<ISignupFormValues>;
  countries: Country[];
  disabled: boolean;
}

export const SignupFormInputs: FC<SignupFormInputsProps> = ({
  formik,
  countries,
  disabled,
}) => {
  const { t } = useTranslation();

  const nationalityOptions = useMemo(() => {
    return countries.map((country) => ({
      label: t(`nationalities.${country.iso_code}`),
      value: country.id,
    }));
  }, [countries, t]);

  const onChangeNationality = useCallback(
    (newNationalityId: string) => {
      if (formik.values.nationalityId === newNationalityId) {
        return;
      }

      formik.setValues((values) => ({
        ...values,
        nationalityId: newNationalityId,
      }));
      formik.setFieldError('nationalityId', undefined);
    },
    [formik]
  );

  const onNationalityBlur = useCallback(() => {
    formik.setFieldTouched('nationalityId', true);
  }, [formik]);

  const countryOptions = useMemo(() => {
    return countries.map((country) => ({
      label: t(`countries.${country.iso_code}`),
      value: country.id,
    }));
  }, [countries, t]);

  const onChangeCountryOfResidence = useCallback(
    (newCountryId: string) => {
      if (formik.values.countryOfResidenceId === newCountryId) {
        return;
      }

      formik.setValues((values) => ({
        ...values,
        countryOfResidenceId: newCountryId,
      }));
      formik.setFieldError('countryOfResidenceId', undefined);
    },
    [formik]
  );

  const onCountryOfResidenceBlur = useCallback(() => {
    formik.setFieldTouched('countryOfResidenceId', true);
  }, [formik]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <FTextInput
          id="firstName"
          name="firstName"
          label={t('mangopay.signup.form.first-name')}
          formik={formik}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <FTextInput
          id="lastName"
          name="lastName"
          label={t('mangopay.signup.form.last-name')}
          formik={formik}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12}>
        <FDatePicker
          dataCy="birthdate"
          id="birthdate"
          name="birthdate"
          label={t('mangopay.signup.form.birthdate')}
          maxDate={new Date()}
          formik={formik}
          openTo={formik.values.birthdate ? 'day' : 'year'}
        />
      </Grid>
      <Grid item xs={12}>
        <CountrySelector
          name="nationalityId"
          label={t('mangopay.signup.form.nationality')}
          onChangeCountry={onChangeNationality}
          onBlur={onNationalityBlur}
          countries={nationalityOptions}
          countryId={formik.values.nationalityId}
          disabled={disabled}
          errorMessage={
            getIn(formik.touched, 'nationalityId') &&
            getIn(formik.errors, 'nationalityId')
          }
        />
      </Grid>
      <Grid item xs={12}>
        <CountrySelector
          name="countryOfResidenceId"
          label={t('mangopay.signup.form.country-of-residence')}
          onChangeCountry={onChangeCountryOfResidence}
          onBlur={onCountryOfResidenceBlur}
          countries={countryOptions}
          countryId={formik.values.countryOfResidenceId}
          disabled={disabled}
          errorMessage={
            getIn(formik.touched, 'countryOfResidenceId') &&
            getIn(formik.errors, 'countryOfResidenceId')
          }
        />
      </Grid>
      <Grid item xs={12}>
        <ResidentRelationshipOptions formik={formik} disabled={disabled} />
      </Grid>
      <Grid item xs={12}>
        <FormControlLabel
          label={t('mangopay.signup.form.accept-terms')}
          title={t('mangopay.signup.form.accept-terms')}
          sx={{ color: 'grey.500' }}
          control={
            <FCheckbox
              id="acceptTerms"
              name="acceptTerms"
              formik={formik}
              disabled={disabled}
              size="small"
              sx={{
                mt: '-1px',
                '& .MuiSvgIcon-root': {
                  color: formik.values.acceptTerms
                    ? 'primary.main'
                    : 'grey.500',
                },
              }}
            />
          }
        />
      </Grid>
    </Grid>
  );
};
